import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Button,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { useLocation, useHistory } from "react-router-dom";
import useSEO from "../../../../seo/use-seo";
import StandardPageHelmet from "../../../../seo/components/StandardPageHelmet";
import CreatorPost from "../MyFeed-explore/creator-post";
import { getMyLikedPosts } from "../../../../api/userApi";
import {
  getBookmarkCollectionsAPI,
  getBookmarkCollectionAPI,
} from "../../../../api/userApi";
import { showToast } from "../../../../utils/toast";
import ArrowsSort from "../../../../assets/ArrowsSort";
import { useSearchPanelStyles } from "../../Home/Search-panel/style";
import UniversalTabs from "../../../elements/UniversalTabs";

export type SavedLikedTab = "Saved" | "Liked";

export default function LikedPosts({ visionType }: { visionType: string }) {
  const seo = useSEO("liked-posts");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const isMobileSm = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const history = useHistory();
  const classes = useSearchPanelStyles();

  const searchParams = new URLSearchParams(location.search);
  const initialTab = ((): SavedLikedTab => {
    const t = searchParams.get("tab");
    if (t === "saved" || t === "liked")
      return t === "saved" ? "Saved" : "Liked";
    return "Liked";
  })();
  const initialSortBy = ((): "popular" | "recent" => {
    const urlSortBy = searchParams.get("sortBy");
    if (urlSortBy === "recent" || urlSortBy === "popular")
      return urlSortBy as "popular" | "recent";
    const savedSortBy = localStorage.getItem("sortBy");
    if (savedSortBy === "recent" || savedSortBy === "popular")
      return savedSortBy as "popular" | "recent";
    return "popular";
  })();

  const [tab, setTab] = useState<SavedLikedTab>(initialTab);
  const [sortBy, setSortBy] = useState<"popular" | "recent">(initialSortBy);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const currentCollectionIdRef = useRef<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const tabs = [
    { label: "Saved", value: "Saved" },
    { label: "Liked", value: "Liked" },
  ];

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("tab");
    if (t === "saved") {
      setTab("Saved");
    } else if (t === "liked") {
      setTab("Liked");
    }
  }, [location.search]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isFilterOpen &&
        dropdownRef.current &&
        filterButtonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !filterButtonRef.current.contains(event.target as Node)
      ) {
        setIsFilterOpen(false);
      }
    };
    if (isFilterOpen)
      document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isFilterOpen]);

  const updateTab = (newTab: SavedLikedTab) => {
    setTab(newTab);
    const params = new URLSearchParams(location.search);
    params.set("tab", newTab === "Saved" ? "saved" : "liked");
    history.push({ pathname: location.pathname, search: params.toString() });
  };

  const updateSortBy = (value: "popular" | "recent") => {
    setSortBy(value);
    setIsFilterOpen(false);
    localStorage.setItem("sortBy", value);
    const params = new URLSearchParams(location.search);
    params.set("sortBy", value);
    history.push(`${location.pathname}?${params.toString()}`);
  };

  const customFetchData = async (page: number) => {
    if (tab === "Liked") {
      try {
        const data = await getMyLikedPosts({
          page,
          perPage: 4,
          sortBy: sortBy === "recent" ? "recent" : "popular",
        });
        const transformedItems = (data.items || []).map((item: any) => {
          const creator = item.creator || item.user;
          return {
            ...item,
            creator,
            user: creator,
            isLiked: true,
          };
        });
        return {
          items: transformedItems,
          totalCount: data.totalCount || 0,
          totalPages: data.totalPages || 0,
          message: data.message,
        };
      } catch (err) {
        showToast(
          (err as any).message || "Failed to load liked posts",
          "error"
        );
        return { items: [], totalCount: 0, totalPages: 0 };
      }
    }

    // Saved = bookmarks
    try {
      let collectionId = currentCollectionIdRef.current;
      if (!collectionId) {
        const collectionsData = await getBookmarkCollectionsAPI();
        const collections = collectionsData?.data?.collections || [];
        const defaultCollection = collections.find((c: any) => c.isDefault);
        if (!defaultCollection) {
          return { items: [], totalCount: 0, totalPages: 0 };
        }
        collectionId = defaultCollection._id;
        currentCollectionIdRef.current = collectionId;
      }

      const collectionData = await getBookmarkCollectionAPI(collectionId);
      let items = collectionData?.data?.items || [];
      if (sortBy === "recent") {
        items = [...items].sort(
          (a: any, b: any) =>
            new Date(b.addedAt || 0).getTime() -
            new Date(a.addedAt || 0).getTime()
        );
      }
      const totalCount = items.length;
      const totalPages = Math.ceil(totalCount / 20);
      const startIndex = (page - 1) * 20;
      const endIndex = startIndex + 20;
      const paginatedItems = items.slice(startIndex, endIndex);

      const transformedItems = paginatedItems.map((item: any) => {
        const post = item.postId || {};
        return {
          ...post,
          creator: post.creator || post.user,
          user: post.creator || post.user,
          isBookmarked: true,
          addedAt: item.addedAt,
        };
      });

      return {
        items: transformedItems,
        totalCount,
        totalPages,
      };
    } catch (err) {
      showToast((err as any).message || "Failed to load bookmarks", "error");
      return { items: [], totalCount: 0, totalPages: 0 };
    }
  };

  return (
    <>
      <StandardPageHelmet seo={seo} />
      <Box style={{ padding: isMobile ? "8px" : "16px" }}>
        <Box
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          marginBottom="24px"
          style={{ gap: 12 }}
        >
          <Box
            className={classes.tabWrapper}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              width: isMobileSm ? "100%" : "auto",
            }}
          >
            <UniversalTabs
              tabs={tabs}
              value={tab}
              onChange={v => updateTab(v as SavedLikedTab)}
            />
          </Box>

          <Box flexGrow={1} />

          <Box
            display="flex"
            alignItems="center"
            flexShrink={0}
            style={{
              gap: isMobileSm ? 8 : 12,
              marginLeft: "auto",
              justifyContent: "flex-end",
              position: "relative",
            }}
          >
            {!isMobileSm && (
              <Button
                ref={filterButtonRef}
                onClick={() => setIsFilterOpen(prev => !prev)}
                className={classes.filterButton}
                disableRipple
              >
                <div
                  style={{
                    marginRight: 8,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <ArrowsSort />
                </div>
                <div style={{ marginRight: 4, fontSize: 20 }}>
                  <b>
                    {sortBy === "popular"
                      ? "Popular"
                      : sortBy === "recent"
                        ? "Newest"
                        : "Filter"}
                  </b>{" "}
                </div>
                <span style={{ fontSize: 20 }}>first</span>
              </Button>
            )}

            {isFilterOpen && !isMobileSm && (
              <div
                ref={dropdownRef}
                className={classes.dropdown}
                style={
                  isMobileSm ? { right: 0, top: 44, width: 110 } : undefined
                }
              >
                <div
                  className={classes.option}
                  style={{ fontWeight: 400 }}
                  onClick={() => updateSortBy("popular")}
                >
                  Popular
                </div>
                <div
                  className={classes.option}
                  style={{ fontWeight: 400 }}
                  onClick={() => updateSortBy("recent")}
                >
                  Newest
                </div>
              </div>
            )}
          </Box>
        </Box>

        {isMobileSm && (
          <Box
            display="flex"
            alignItems="center"
            marginBottom={2}
            style={{ gap: 12, position: "relative" }}
          >
            <Button
              ref={filterButtonRef}
              onClick={() => setIsFilterOpen(prev => !prev)}
              style={{
                borderRadius: 8,
                color: "#FFFFFF",
                padding: "8px 12px",
                textTransform: "none",
                justifyContent: "center",
                fontSize: 14,
              }}
            >
              <div
                style={{
                  marginRight: 8,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <ArrowsSort />
              </div>
              <div style={{ marginRight: 4 }}>
                <b>
                  {sortBy === "popular"
                    ? "Popular"
                    : sortBy === "recent"
                      ? "Newest"
                      : "Filter"}
                </b>{" "}
              </div>
              <span style={{ fontSize: 12 }}>first</span>
            </Button>
            {isFilterOpen && (
              <div
                ref={dropdownRef}
                style={{
                  position: "absolute",
                  background: "#2A2A2A",
                  borderRadius: 6,
                  marginTop: 6,
                  zIndex: 1000,
                  top: 44,
                  left: 0,
                  width: 110,
                  padding: 8,
                  gap: 4,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  onClick={() => updateSortBy("popular")}
                  style={{
                    padding: 8,
                    borderRadius: 6,
                    cursor: "pointer",
                    fontSize: 14,
                    color: "#FFFFFF",
                  }}
                >
                  Popular
                </div>
                <div
                  onClick={() => updateSortBy("recent")}
                  style={{
                    padding: 8,
                    borderRadius: 6,
                    cursor: "pointer",
                    fontSize: 14,
                    color: "#FFFFFF",
                  }}
                >
                  Newest
                </div>
              </div>
            )}
          </Box>
        )}

        <Box
          style={{
            width: "100%",
            marginLeft: "auto",
            marginRight: "auto",
            maxWidth: 580,
          }}
        >
          <CreatorPost
            key={`${tab}-${sortBy}`}
            visionType="My Subscription"
            customFetchData={customFetchData}
            notFoundText={tab === "Liked" ? "liked posts" : "saved posts"}
          />
        </Box>
      </Box>
    </>
  );
}
